import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Typography, Box, IconButton, useTheme } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MessageIcon from '@mui/icons-material/Message';
import { broadCastMessageOperations } from '../../../redux/slices/operator';
import { dispatch } from '../../../redux/store';

const Messages = () => {
  const messages = useSelector((state) => state?.operator?.broadCastMessage?.data || []);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    dispatch(broadCastMessageOperations());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sort messages by createdAt in descending order
  const sortedMessages = [...messages].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  // Format date in "23-Dec-2024 1:08:08 PM" format
  const formatDate = (date) => {
    const options = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    };
    return new Intl.DateTimeFormat('en-US', options).format(new Date(date));
  };

  return (
    <Box
      sx={{
        padding: { xs: 2, md: 3 },
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(22,28,36,0.8)'
          : '#f5f5f5',
        minHeight: '100vh',
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          // mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              mr: 2,
              width: 35,
              height: 35,
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.1)'
                : 'rgba(0,0,0,0.05)',
              backdropFilter: 'blur(6px)',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.2)'
                  : 'rgba(0,0,0,0.1)',
                transform: 'translateX(-3px)',
              },
              transition: 'all 0.2s',
              border: `1px solid ${theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.12)'
                : 'rgba(0,0,0,0.08)'
                }`,
            }}
          >
            <ArrowBackIcon
              sx={{
                fontSize: 20,
                color: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.8)'
                  : 'rgba(0,0,0,0.7)'
              }}
            />
          </IconButton>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(45deg, #f3ec78 0%, #af4261 100%)'
                : 'linear-gradient(45deg, #2c3e50 30%, #3498db 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textTransform: 'uppercase',
              letterSpacing: '0.1rem',
              textAlign: 'center',
            }}
          >
            NOTIFICATIONS
          </Typography>
        </Box>
      </Box>

      {/* Messages Section */}
      {sortedMessages.map((message) => (
        <Card
          key={message._id}
          sx={{
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#d0d0d0'}`,
            borderRadius: '12px',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 12px rgba(0,0,0,0.2)'
              : '0 4px 12px rgba(0,0,0,0.05)',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(45,45,60,0.95)' : '#ffffff',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 6px 16px rgba(0,0,0,0.3)'
                : '0 6px 16px rgba(0,0,0,0.1)',
            },
          }}
        >
          <CardContent sx={{ padding: '16px' }}>
            <Typography
              dangerouslySetInnerHTML={{
                __html: message.message.replace(/\n/g, '<br />'),
              }}
              sx={{
                fontSize: '0.95rem',
                lineHeight: '1.5',
                color: theme.palette.mode === 'dark' ? '#fff' : '#333',
                marginBottom: '8px',
              }}
            />
            <Typography
              sx={{
                textAlign: 'right',
                fontSize: '0.75rem',
                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : '#888',
                marginTop: '8px',
              }}
            >
              {formatDate(message.createdAt)}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default Messages;
